import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramDuctLeakageClass } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.ductLeakageClass.v1';

type Fields = {
  leakageFlow: string;
  leakageClass: string;
  testPressure: string;
  surfaceArea: string;
  exponent: string;
};
const EMPTY: Fields = {
  leakageFlow: '',
  leakageClass: '',
  testPressure: '',
  surfaceArea: '',
  exponent: '0.65',
};

/**
 * Duct Leakage Class (SMACNA-style) — Q = CL × P^n × (A / 100).
 * Bidirectional: fill any 3 of {Q, CL, P, A}; n is a separate exponent (default 0.65).
 */
const AirflowDuctLeakageClassScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);

    const n = Number(fields.exponent);
    if (!Number.isFinite(n) || n <= 0 || n > 2) {
      setError('Exponent n must be a reasonable number greater than zero.');
      return;
    }

    const core = [fields.leakageFlow, fields.leakageClass, fields.testPressure, fields.surfaceArea];
    const filled = core.filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of {Q, CL, P, A} to solve the 4th (exponent n is a parameter).'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const q = toImp(fields.leakageFlow, 'flow');
    const cl = Number(fields.leakageClass);
    const p = toImp(fields.testPressure, 'pressure');
    const a = toImp(fields.surfaceArea, 'area');

    if (fields.leakageFlow === '') {
      if (!(cl >= 0) || !(p > 0) || !(a > 0)) { setError('CL ≥ 0, and P, A must be > 0.'); return; }
      setSolvedFor('leakageFlow');
      set({ leakageFlow: fromImp(cl * Math.pow(p, n) * (a / 100), 'flow', 1) });
    } else if (fields.leakageClass === '') {
      if (!(q >= 0) || !(p > 0) || !(a > 0)) { setError('Q ≥ 0, and P, A must be > 0.'); return; }
      const cl2 = q / (Math.pow(p, n) * (a / 100));
      setSolvedFor('leakageClass');
      set({ leakageClass: parseFloat(cl2.toFixed(3)).toString() });
    } else if (fields.testPressure === '') {
      if (!(q > 0) || !(cl > 0) || !(a > 0)) { setError('Q, CL, A must be > 0.'); return; }
      const pImp = Math.pow(q / (cl * (a / 100)), 1 / n);
      setSolvedFor('testPressure');
      set({ testPressure: fromImp(pImp, 'pressure', 4) });
    } else if (fields.surfaceArea === '') {
      if (!(q >= 0) || !(cl > 0) || !(p > 0)) { setError('CL, P must be > 0; Q ≥ 0.'); return; }
      const aImp = 100 * q / (cl * Math.pow(p, n));
      setSolvedFor('surfaceArea');
      set({ surfaceArea: fromImp(aImp, 'area', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.leakageFlow && { label: 'Leakage Q', value: fields.leakageFlow, unit: unit('flow'), emphasis: solvedFor === 'leakageFlow' },
    fields.leakageClass && { label: 'Leakage Class CL', value: fields.leakageClass, emphasis: solvedFor === 'leakageClass' },
    fields.testPressure && { label: 'Test Pressure P', value: fields.testPressure, unit: unit('pressure'), emphasis: solvedFor === 'testPressure' },
    fields.surfaceArea && { label: 'Surface Area A', value: fields.surfaceArea, unit: unit('area'), emphasis: solvedFor === 'surfaceArea' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Duct Leakage (Class Method)"
      subtitle="Fill any 3 of {Q, CL, P, A}; n is a parameter (default 0.65)"
      formula={
        <>
          <FormulaLine>Q = CL × P^n × (A / 100)</FormulaLine>
          <FormulaNote>
            Q = leakage ({unit('flow')}), CL = leakage class, P = test pressure ({unit('pressure')}), A = duct surface area ({unit('area')}),
            n ≈ 0.65 typical.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q = CL × P^n × (A / 100)"
      diagram={<DiagramDuctLeakageClass />}
      outputSlots={{
        cfm: fields.leakageFlow ? toImp(fields.leakageFlow, 'flow') : undefined,
        static_pressure_iwg: fields.testPressure ? toImp(fields.testPressure, 'pressure') : undefined,
        area_sqft: fields.surfaceArea ? toImp(fields.surfaceArea, 'area') : undefined,
        leakage_cfm_per_100sqft:
          fields.leakageFlow && fields.surfaceArea
            ? (toImp(fields.leakageFlow, 'flow') / toImp(fields.surfaceArea, 'area')) * 100
            : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.leakageFlow) {
          patch.leakageFlow = fromImp(values.cfm, 'flow', 1);
        }
        if (values.static_pressure_iwg != null && !fields.testPressure) {
          patch.testPressure = fromImp(values.static_pressure_iwg, 'pressure', 4);
        }
        if (values.area_sqft != null && !fields.surfaceArea) {
          patch.surfaceArea = fromImp(values.area_sqft, 'area', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Leakage Q (${unit('flow')})`}
          value={fields.leakageFlow}
          onChangeText={(v) => set({ leakageFlow: v })}
          rightText={unit('flow')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Leakage Class (CL)"
          value={fields.leakageClass}
          onChangeText={(v) => set({ leakageClass: v })}
          placeholder="Example: 6"
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Test Pressure P (${unit('pressure')})`}
          value={fields.testPressure}
          onChangeText={(v) => set({ testPressure: v })}
          rightText={unit('pressure')}
          placeholder="Example: 1.0"
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Duct Surface Area A (${unit('area')})`}
          value={fields.surfaceArea}
          onChangeText={(v) => set({ surfaceArea: v })}
          rightText={unit('area')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Exponent n"
          value={fields.exponent}
          onChangeText={(v) => set({ exponent: v })}
          placeholder="0.65 typical"
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowDuctLeakageClassScreen;
